<script>
import AppPagination from '@/components/common/AppPagination.vue'
import { createB2bCompanyCategory, getB2bCompanyCategories, updateB2bCompanyCategory } from '@/api/b2b.js'

export default {
  name: 'B2bCompanyCategoryPage',
  components: { AppPagination },
  data() {
    return {
      search: { keyword: '', status: '' },
      statusOptions: [
        { title: '전체', value: '' },
        { title: '사용', value: 'Y' },
        { title: '미사용', value: 'N' },
      ],
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      dialog: false,
      saving: false,
      editTarget: null,
      form: { categoryName: '', description: '', sortOrder: 0, activeYn: 'Y' },
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getB2bCompanyCategories({
          keyword: this.search.keyword || undefined,
          status: this.search.status || undefined,
          page: this.page,
          size: this.size,
        })
        this.items = res.data?.data?.items || []
        this.total = res.data?.data?.total || 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', status: '' }
      this.handleSearch()
    },
    openCreate() {
      this.editTarget = null
      this.form = { categoryName: '', description: '', sortOrder: 0, activeYn: 'Y' }
      this.dialog = true
    },
    openEdit(item) {
      this.editTarget = item
      this.form = {
        categoryName: item.categoryName || '',
        description: item.description || '',
        sortOrder: item.sortOrder || 0,
        activeYn: item.activeYn || 'Y',
      }
      this.dialog = true
    },
    async save() {
      if (!this.form.categoryName?.trim()) return
      this.saving = true
      try {
        const res = this.editTarget
          ? await updateB2bCompanyCategory(this.editTarget.b2bCompanyCategoryCode, this.form)
          : await createB2bCompanyCategory(this.form)
        if (res.data?.code === 0) {
          this.dialog = false
          this.fetchList()
        }
      } finally {
        this.saving = false
      }
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">B2B 업체분류 관리</div>
        <div class="text-body-2 text-grey mt-1">기업 특가 대상 업체를 분류합니다.</div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">분류 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mb-5">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field v-model="search.keyword" label="분류 검색" variant="outlined" density="compact" hide-details clearable @keyup.enter="handleSearch" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="search.status" :items="statusOptions" label="사용여부" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" md="4" class="d-flex justify-end ga-2">
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
            <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-title class="text-subtitle-1 font-weight-bold">분류 목록 <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip></v-card-title>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table density="compact">
        <thead>
          <tr>
            <th>분류명</th>
            <th>설명</th>
            <th>정렬</th>
            <th>사용</th>
            <th>등록일</th>
            <th class="text-end">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.b2bCompanyCategoryCode">
            <td class="font-weight-bold">{{ item.categoryName }}</td>
            <td>{{ item.description || '-' }}</td>
            <td>{{ item.sortOrder }}</td>
            <td><v-chip size="small" :color="item.activeYn === 'Y' ? 'primary' : 'grey'">{{ item.activeYn }}</v-chip></td>
            <td>{{ formatDate(item.createdDt) }}</td>
            <td class="text-end"><v-btn icon="mdi-pencil" size="small" variant="text" @click="openEdit(item)" /></td>
          </tr>
          <tr v-if="!loading && !items.length"><td colspan="6" class="text-center text-grey py-8">등록된 업체분류가 없습니다.</td></tr>
        </tbody>
      </v-table>
      <div class="px-4 pb-4">
        <AppPagination :total="total" :page="page" :size="size" @update:page="p => { page = p; fetchList() }" @update:size="s => { size = s; page = 1; fetchList() }" />
      </div>
    </v-card>

    <v-dialog v-model="dialog" max-width="560">
      <v-card rounded="lg">
        <v-card-title>{{ editTarget ? '분류 수정' : '분류 등록' }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.categoryName" label="분류명 *" variant="outlined" />
          <v-textarea v-model="form.description" label="설명" rows="3" variant="outlined" />
          <v-row>
            <v-col cols="6"><v-text-field v-model.number="form.sortOrder" type="number" label="정렬순서" variant="outlined" /></v-col>
            <v-col cols="6"><v-select v-model="form.activeYn" :items="[{ title: '사용', value: 'Y' }, { title: '미사용', value: 'N' }]" label="사용여부" variant="outlined" /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
